import { includesIgnoringAccents } from "../utils/sbcListControls";
export const EVOLUTION_STATE_ITEMS = [
    { state: "unachieved", label: "Not Unlocked" },
    { state: "available", label: "Available" },
    { state: "planned", label: "Planned" },
    { state: "inProgress", label: "In Progress" },
    { state: "completed", label: "Completed" },
];
const EXPIRE_SOON_THRESHOLD_MS = 1000 * 60 * 60 * 24 * 3; // 3 days
/**
 * Within three days of its unlock deadline. `now` is injected so the result is
 * a function of its inputs — callers pass `Date.now()`.
 */
export const isExpiringSoon = (evo, now) => {
    if (evo.expired || !evo.unlockBy)
        return false;
    const msRemaining = evo.unlockBy * 1000 - now;
    return msRemaining > 0 && msRemaining <= EXPIRE_SOON_THRESHOLD_MS;
};
/** A cost's event tokens as id + amount, in token id order. */
export const costTokens = (cost) => Object.entries(cost?.tokens ?? {})
    .filter(([, amount]) => amount > 0)
    .map(([eventTokenId, amount]) => ({
    eventTokenId: Number(eventTokenId),
    amount,
}))
    .sort((a, b) => a.eventTokenId - b.eventTokenId);
export const isFreeEvolution = (evo) => !evo.cost ||
    (evo.cost.coins === 0 &&
        evo.cost.points === 0 &&
        costTokens(evo.cost).length === 0);
export const isPaidEvolution = (evo) => !!evo.cost &&
    (evo.cost.coins > 0 ||
        evo.cost.points > 0 ||
        costTokens(evo.cost).length > 0);
/**
 * The default selection: everything the user can start right now, minus the
 * evolutions the backend marks `excludedByDefault`.
 */
export const availableEvolutionIds = (evolutions, ignoreExcludedByDefault = false) => evolutions
    .filter((evo) => evo.state === "available" &&
    (ignoreExcludedByDefault || !evo.excludedByDefault))
    .map((evo) => evo.id);
/**
 * The included set after the catalogue is fetched again (a pull to refresh,
 * or a saved build changing evolution states). A set the user never touched
 * follows the new catalogue's default; a hand-picked one is kept, minus
 * evolutions the catalogue no longer has.
 */
export const includedAfterRefetch = ({ included, previous, next, }) => {
    const previousDefault = availableEvolutionIds(previous);
    const untouched = previous.length === 0 ||
        (included.length === previousDefault.length &&
            previousDefault.every((id) => included.includes(id)));
    if (untouched)
        return availableEvolutionIds(next.evolutions);
    const known = new Set([...next.evolutions, ...next.expiredEvolutions].map((evo) => evo.id));
    return included.filter((id) => known.has(id));
};
// ---------------------------------------------------------------------------
// Draft transitions. Each returns a new draft and never mutates its input, so
// they drop straight into a useReducer or a redux reducer.
// ---------------------------------------------------------------------------
/** Included but not required — clears any must-include on the same evolution. */
export const includeEvolution = (draft, evolutionId) => ({
    included: draft.included.includes(evolutionId)
        ? draft.included
        : [...draft.included, evolutionId],
    mustInclude: draft.mustInclude.filter((id) => id !== evolutionId),
});
/** Required in every chain. Implies included, so it lands in both lists. */
export const mustIncludeEvolution = (draft, evolutionId) => ({
    included: draft.included.includes(evolutionId)
        ? draft.included
        : [...draft.included, evolutionId],
    mustInclude: draft.mustInclude.includes(evolutionId)
        ? draft.mustInclude
        : [...draft.mustInclude, evolutionId],
});
export const excludeEvolution = (draft, evolutionId) => ({
    included: draft.included.filter((id) => id !== evolutionId),
    mustInclude: draft.mustInclude.filter((id) => id !== evolutionId),
});
/** "Include All" over whatever the current selection is showing. */
export const includeEvolutions = (draft, evolutionIds) => {
    const ids = new Set(evolutionIds);
    return {
        included: [...draft.included.filter((id) => !ids.has(id)), ...evolutionIds],
        mustInclude: draft.mustInclude,
    };
};
/** "Exclude All" — drops the ids from both lists. */
export const excludeEvolutions = (draft, evolutionIds) => {
    const ids = new Set(evolutionIds);
    return {
        included: draft.included.filter((id) => !ids.has(id)),
        mustInclude: draft.mustInclude.filter((id) => !ids.has(id)),
    };
};
export const resetDraft = (allAvailableIds) => ({
    included: [...allAvailableIds],
    mustInclude: [],
});
export const evolutionDraftState = (draft, evolutionId) => {
    if (draft.mustInclude.includes(evolutionId))
        return "mustInclude";
    if (draft.included.includes(evolutionId))
        return "include";
    return "exclude";
};
const sameIds = (a, b) => {
    if (a.length !== b.length)
        return false;
    const set = new Set(a);
    return b.every((id) => set.has(id));
};
/** Whether Apply should be enabled. */
export const draftHasChanges = (draft, initial) => !sameIds(draft.included, initial.included) ||
    !sameIds(draft.mustInclude, initial.mustInclude);
/**
 * Whether the draft equals what "Reset" would produce — every available
 * evolution included and nothing required. Drives the disabled Reset button.
 */
export const isDraftAtDefault = (draft, allAvailableIds) => {
    if (draft.mustInclude.length !== 0)
        return false;
    const included = new Set(draft.included);
    if (included.size !== allAvailableIds.length)
        return false;
    return allAvailableIds.every((id) => included.has(id));
};
// ---------------------------------------------------------------------------
// Selection
// ---------------------------------------------------------------------------
export const isSameSelection = (a, b) => {
    if (a.type !== b.type)
        return false;
    if (a.type === "state" && b.type === "state")
        return a.state === b.state;
    if (a.type === "category" && b.type === "category")
        return a.categoryId === b.categoryId;
    if (a.type === "other" && b.type === "other")
        return a.key === b.key;
    return true; // both "all-included"
};
export const evolutionPickerSelectionLabel = (selection, orderedCategories) => {
    switch (selection.type) {
        case "all-included":
            return "All Included";
        case "state":
            return (EVOLUTION_STATE_ITEMS.find((i) => i.state === selection.state)?.label ??
                "Evolutions");
        case "category":
            return (orderedCategories.find((c) => c.id === selection.categoryId)?.name ??
                "Evolutions");
        case "other":
            return {
                new: "New Evolutions",
                expiring: "Expiring Soon",
                free: "Free",
                paid: "Paid",
                all: "All",
                expired: "Expired",
            }[selection.key];
    }
};
/**
 * The evolutions the picker should list, in order.
 *
 * A non-empty search overrides the selection and matches across every
 * category — the panel selection only narrows the *browse* case.
 */
export const visibleEvolutions = ({ evolutions, expiredEvolutions, selection, searchTerm, draft, now, stickyIncludedIds, showExpired, }) => {
    const isExpiredSelection = selection.type === "other" && selection.key === "expired";
    const source = isExpiredSelection ? expiredEvolutions : evolutions;
    const trimmed = searchTerm.trim();
    if (trimmed) {
        const searchable = isExpiredSelection
            ? expiredEvolutions
            : showExpired
                ? [...evolutions, ...expiredEvolutions]
                : evolutions;
        return searchable.filter((evo) => includesIgnoringAccents(evo.name, trimmed));
    }
    switch (selection.type) {
        case "all-included": {
            const included = stickyIncludedIds ?? new Set(draft.included);
            return source.filter((evo) => included.has(evo.id));
        }
        case "state":
            return source.filter((evo) => evo.state === selection.state);
        case "category":
            return source.filter((evo) => evo.categoryId === selection.categoryId);
        case "other":
            switch (selection.key) {
                case "new":
                    return source.filter((evo) => evo.newEvolution);
                case "expiring":
                    return source.filter((evo) => isExpiringSoon(evo, now));
                case "free":
                    return source.filter(isFreeEvolution);
                case "paid":
                    return source.filter(isPaidEvolution);
                case "all":
                case "expired":
                    return source;
            }
    }
};
/**
 * Must-include first, then included, then excluded.
 *
 * `priorities` is a snapshot taken when the selection changed, not the live
 * draft: sorting on the live draft would make a card jump out from under the
 * finger the moment it was toggled.
 */
export const sortByDraftPriority = (evolutions, priorities) => [...evolutions].sort((a, b) => (priorities.get(a.id) ?? 2) - (priorities.get(b.id) ?? 2));
/** The snapshot `sortByDraftPriority` consumes. */
export const draftPrioritySnapshot = (evolutions, draft) => {
    const mustInclude = new Set(draft.mustInclude);
    const included = new Set(draft.included);
    const snapshot = new Map();
    for (const evo of evolutions) {
        snapshot.set(evo.id, mustInclude.has(evo.id) ? 0 : included.has(evo.id) ? 1 : 2);
    }
    return snapshot;
};
export const groupEvolutionsByCategory = (evolutions, orderedCategories) => {
    const groups = new Map();
    for (const evo of evolutions) {
        const list = groups.get(evo.categoryId) ?? [];
        list.push(evo);
        groups.set(evo.categoryId, list);
    }
    return orderedCategories
        .filter((cat) => groups.has(cat.id))
        .map((cat) => ({ category: cat, evolutions: groups.get(cat.id) }));
};
export const evolutionPickerCounts = (evolutions, expiredEvolutions, now) => {
    const byState = {};
    const byCategory = {};
    let newEvolutions = 0;
    let expiringSoon = 0;
    let free = 0;
    let paid = 0;
    for (const evo of evolutions) {
        byState[evo.state] = (byState[evo.state] ?? 0) + 1;
        byCategory[evo.categoryId] = (byCategory[evo.categoryId] ?? 0) + 1;
        if (evo.newEvolution)
            newEvolutions++;
        if (isExpiringSoon(evo, now))
            expiringSoon++;
        if (isFreeEvolution(evo))
            free++;
        if (isPaidEvolution(evo))
            paid++;
    }
    return {
        byState,
        byCategory,
        newEvolutions,
        expiringSoon,
        free,
        paid,
        all: evolutions.length,
        expired: expiredEvolutions.length,
    };
};
//# sourceMappingURL=picker.js.map